import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Button, ConfigProvider, Form, Input, Spin, message, Checkbox } from 'antd';
import { WalletOutlined, CloseOutlined } from '@ant-design/icons';
import { apiRequests } from 'src/shared/api/api';
import { PublicOfferContent } from 'src/shared/content/PublicOfferContent';

const hideNumberSpinnerStyles = `
  .no-spinner input[type="number"]::-webkit-inner-spin-button,
  .no-spinner input[type="number"]::-webkit-outer-spin-button,
  .no-spinner .ant-input-number-input::-webkit-inner-spin-button,
  .no-spinner .ant-input-number-input::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  .no-spinner input[type="number"],
  .no-spinner .ant-input-number-input {
    -moz-appearance: textfield;
  }
`;

export function UpgradeSubscriptionModal({ open, onCancel, token }) {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [tariffs, setTariffs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [accountInfo, setAccountInfo] = useState(null);
  const [accountInfoLoading, setAccountInfoLoading] = useState(false);
  const [isPublicOfferVisible, setIsPublicOfferVisible] = useState(false);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [discountInfo, setDiscountInfo] = useState(null);
  const [withoutAcquiring, setWithoutAcquiring] = useState(false);
  const [form] = Form.useForm();

  const isAdmin = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('admin') !== null;
  };

  const showPublicOffer = (e) => {
    e.preventDefault();
    setIsPublicOfferVisible(true);
  };

  const calculateDiscount = useCallback((accountData) => {
    if (!selectedPlan || !accountData) return;
    
    const selectedTariff = tariffs.find(t => t.id === selectedPlan);
    if (!selectedTariff || !selectedTariff.offer_hours || !selectedTariff.discount_percent) {
      setDiscountInfo(null);
      return;
    }

    let cashboxCreatedAt;
    if (accountData.demo_expiration && accountData.demo_period) {
      cashboxCreatedAt = accountData.demo_expiration - (accountData.demo_period * 24 * 3600);
    } else {
      setDiscountInfo(null);
      return;
    }
    
    const currentTime = Date.now() / 1000;
    const hoursSinceCreation = (currentTime - cashboxCreatedAt) / 3600;
    
    const isFirstPayment = accountData.balance === 0 || accountData.balance === null;
    
    if (hoursSinceCreation >= 0 && hoursSinceCreation <= selectedTariff.offer_hours && isFirstPayment) {
      const hoursRemaining = Math.max(0, Math.min(selectedTariff.offer_hours, Math.floor(selectedTariff.offer_hours - hoursSinceCreation)));
      setDiscountInfo({
        percent: selectedTariff.discount_percent,
        offerHours: selectedTariff.offer_hours,
        hoursRemaining: hoursRemaining
      });
    } else {
      setDiscountInfo(null);
    }
  }, [selectedPlan, tariffs]);

  const fetchTariffs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiRequests.account.getTariffs();
      const tariffsList = response.data.result || [];
      setTariffs(tariffsList);
      
      setSelectedPlan((prev) => {
        if (tariffsList.length > 0 && !prev) {
          return tariffsList[0].id;
        }
        return prev;
      });
    } catch (error) {
      console.error('Ошибка при загрузке тарифов:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAccountInfo = useCallback(async () => {
    setAccountInfoLoading(true);
    try {
      const response = await apiRequests.account.getInfo();
      setAccountInfo(response.data);
    } catch (error) {
      console.error('Ошибка при загрузке информации об аккаунте:', error);
    } finally {
      setAccountInfoLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && token) {
      apiRequests.setToken(token);
      fetchTariffs();
      fetchAccountInfo();
    }
  }, [open, token, fetchTariffs, fetchAccountInfo]);

  useEffect(() => {
    if (selectedPlan && accountInfo && tariffs.length > 0) {
      calculateDiscount(accountInfo);
    }
  }, [selectedPlan, accountInfo, tariffs, calculateDiscount]);

  const amountValue = Form.useWatch('amount', form);
  useEffect(() => {
    if (amountValue && accountInfo && discountInfo) {
      // Этот эффект может использоваться для дополнительной логики в будущем
    }
  }, [amountValue, accountInfo, discountInfo]);

  const getCurrentUserId = async () => {
    try {
      const permissionRes = await apiRequests.users.getMyPermissions();
      return permissionRes.data.user_id;
    } catch (error) {
      console.error('Ошибка при получении ID пользователя:', error);
      return null;
    }
  };

  const setAmount = (value) => {
    form.setFieldsValue({ amount: value });
  };

  const handleUpgradePlan = async () => {
    try {
      const values = await form.validateFields();
      const amount = values.amount;
      
      if (!selectedPlan) {
        message.error('Пожалуйста, выберите тариф');
        return;
      }

      setIsPaymentLoading(true);

      if (withoutAcquiring && isAdmin()) {
        const userId = await getCurrentUserId();
        if (!userId) {
          message.error('Не удалось получить ID пользователя');
          setIsPaymentLoading(false);
          return;
        }

        const response = await apiRequests.account.createManualDeposit(
          {
            amount: amount,
            tariff_id: selectedPlan
          },
          userId
        );

        if (response.data) {
          message.success('Баланс успешно пополнен!');
          await fetchAccountInfo();
          setTimeout(() => {
            onCancel();
            window.location.reload();
          }, 1000);
        }
      } else {
        const response = await apiRequests.account.createPayment({
          amount: amount,
          tariff_id: selectedPlan
        });

        if (response.data && response.data.payment_url) {
          window.open(response.data.payment_url, '_blank');
          message.success('Перенаправление на страницу оплаты...');
          setTimeout(() => {
            onCancel();
          }, 1000);
        } else {
          message.error('Не удалось получить ссылку на оплату');
        }
      }
    } catch (error) {
      console.error('Ошибка при создании транзакции:', error);
      if (error.response && error.response.data && error.response.data.detail) {
        message.error(error.response.data.detail);
      } else {
        message.error('Произошла ошибка при создании транзакции');
      }
    } finally {
      setIsPaymentLoading(false);
    }
  };

  return (
    <ConfigProvider theme={{ token: { borderRadiusLG: 18 } }}>
      <style>{hideNumberSpinnerStyles}</style>
      <Modal
        open={open}
        onCancel={onCancel}
        footer={null}
        width={520}
        centered
        title={
          <span style={{ 
            fontWeight: 600, 
            fontSize: '28px', 
            letterSpacing: '-0.05em',
            display: 'flex',
            alignItems: 'center',
            gap: '20px'
          }}>
            <WalletOutlined style={{ fontSize: '24px' }} />
            Обновить подписку
          </span>
        }
        closeIcon={
          <span style={{
            background: 'rgba(0, 0, 0, 0.05)',
            padding: '10px',
            borderRadius: '7px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <CloseOutlined style={{ fontSize: '12px' }} />
          </span>
        }
        styles={{
          body: {
            padding: '10px 10px 15px 10px',
          },
        }}
      >
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '10px'
        }}>
          {accountInfoLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '10px' }}>
              <Spin size="small" />
            </div>
          ) : accountInfo ? (
            <div>
              <p style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: 500,
                marginBottom: '4px'
              }}>
                Текущий баланс
              </p>
              <p style={{
                margin: 0,
                fontSize: '24px',
                fontWeight: 700
              }}>
                {accountInfo.balance?.toLocaleString('ru-RU') || 0} ₽
              </p>
            </div>
          ) : null}

          <div style={{ marginBottom: '0px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '4px', 
              fontSize: '14px',
              fontWeight: 500
            }}>
              План
            </label>
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                <Spin />
              </div>
            ) : tariffs.length > 0 ? (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'row', 
                gap: '12px',
                width: '100%'
              }}>
                {tariffs.map((tariff) => (
                  <div
                    key={tariff.id}
                    onClick={() => setSelectedPlan(tariff.id)}
                    style={{ 
                      height: 'auto',
                      padding: '16px',
                      borderRadius: '8px',
                      border: selectedPlan === tariff.id ? '1px solid #1890ff' : '1px solid #d9d9d9',
                      flex: 1,
                      cursor: 'pointer',
                      backgroundColor: selectedPlan === tariff.id ? '#e6f7ff' : 'white',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                        {tariff.name}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {tariff.price} ₽{tariff.per_user ? ' / пользователь' : ''} {tariff.frequency ? `/ ${tariff.frequency} дней` : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                Тарифы не найдены
              </div>
            )}
          </div>

          <Form 
            form={form} 
            layout="vertical"
            initialValues={{ amount: 1 }}
            style={{ marginTop: '10px' }}
          >
            <Form.Item 
              name="amount" 
              label="Сумма пополнения" 
              rules={[
                { required: true, message: 'Введите сумму пополнения' },
                {
                  validator: (_, value) => {
                    if (value <= 0) {
                      return Promise.reject('Сумма должна быть больше 0');
                    }
                    return Promise.resolve();
                  }
                }
              ]}
              style={{ marginBottom: '8px' }}
            >
              <Input 
                type="number" 
                min={1} 
                placeholder="Введите сумму" 
                suffix="₽"
                style={{ 
                  borderRadius: '8px', 
                  padding: '10px'
                }}
                className="no-spinner"
              />
            </Form.Item>

            {discountInfo && (() => {
              const amount = form.getFieldValue('amount') || 0;
              const discountAmount = amount * (discountInfo.percent / 100);
              const finalAmount = amount - discountAmount;
              
              return (
                <div style={{
                  backgroundColor: '#f0f9ff',
                  border: '1px solid #bae6fd',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '12px'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: amount > 0 ? '8px' : '0'
                  }}>
                    <span style={{ fontSize: '14px', fontWeight: 500, color: '#0369a1' }}>
                        Скидка {discountInfo.percent}% доступна
                    </span>
                    {discountInfo.hoursRemaining > 0 && (
                      <span style={{ fontSize: '12px', color: '#666' }}>
                        Осталось: {discountInfo.hoursRemaining} ч.
                      </span>
                    )}
                  </div>
                  {amount > 0 && (
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      marginTop: '8px',
                      paddingTop: '8px',
                      borderTop: '1px solid #bae6fd',
                      fontSize: '15px',
                      fontWeight: 600,
                      color: '#0369a1'
                    }}>
                      <span>Итого к оплате:</span>
                      <span>{finalAmount.toFixed(2)} ₽</span>
                    </div>
                  )}
                </div>
              );
            })()}
            
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              gap: '8px',
              marginTop: '0px',
              marginBottom: '10px'
            }}>
              <Button 
                style={{ flex: 1, fontWeight: 500 }}
                onClick={() => setAmount(100)}
              >
                100 ₽
              </Button>
              <Button 
                style={{ flex: 1, fontWeight: 500 }}
                onClick={() => setAmount(1000)}
              >
                1 000 ₽
              </Button>
              <Button 
                style={{ flex: 1, fontWeight: 500 }}
                onClick={() => setAmount(10000)}
              >
                10 000 ₽
              </Button>
            </div>
          </Form>

          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: '12px',
            marginTop: '0px'
          }}>
            {isAdmin() && (
              <Checkbox
                checked={withoutAcquiring}
                onChange={(e) => setWithoutAcquiring(e.target.checked)}
                style={{
                  marginBottom: '4px'
                }}
              >
                Пополнить без эквайринга
              </Checkbox>
            )}
            
            <button
              onClick={handleUpgradePlan}
              disabled={isPaymentLoading}
              style={{
                fontWeight: 600,
                fontSize: '16px',
                color: 'white',
                width: '100%',
                background: isPaymentLoading ? '#999' : '#000',
                borderRadius: '12px',
                display: 'flex',
                justifyContent: 'center',
                transition: 'all 0.3s ease-in-out',
                border: 'none',
                cursor: isPaymentLoading ? 'not-allowed' : 'pointer',
                padding: '12px 0',
                marginBottom: '0px',
                opacity: isPaymentLoading ? 0.7 : 1
              }}
              onMouseEnter={(e) => {
                if (!isPaymentLoading) {
                  e.currentTarget.style.transform = 'scale(1.01)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              {isPaymentLoading ? 'Обработка...' : 'Обновить план'}
            </button>
            <p style={{ 
              margin: '0px 0 0 0',
              fontSize: '12px',
              color: '#666',
              textAlign: 'center',
              lineHeight: '1.5'
            }}>
              Нажимая на кнопку, вы соглашаетесь с условиями{' '}
              <span 
                style={{ 
                  color: '#1890ff', 
                  cursor: 'pointer',
                  textDecoration: 'none'
                }}
                onClick={showPublicOffer}
                onMouseEnter={(e) => {
                  e.currentTarget.style.textDecoration = 'underline';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.textDecoration = 'none';
                }}
              >
                публичной оферты
              </span>
            </p>
          </div>
        </div>
      </Modal>

      <Modal
        open={isPublicOfferVisible}
        onCancel={() => setIsPublicOfferVisible(false)}
        footer={null}
        width={650}
        centered
        title={
          <span style={{
            fontWeight: 600,
            fontSize: '24px',
            letterSpacing: '-0.05em'
          }}>
            Публичная оферта
          </span>
        }
        closeIcon={
          <span style={{
            background: 'rgba(0, 0, 0, 0.05)',
            padding: '10px',
            borderRadius: '7px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <CloseOutlined style={{ fontSize: '12px' }} />
          </span>
        }
      >
        <PublicOfferContent />
        <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center' }}>
          <button
            onClick={() => setIsPublicOfferVisible(false)}
            style={{
              fontWeight: 600,
              fontSize: '16px',
              color: 'white',
              width: '100%',
              background: '#000',
              borderRadius: '12px',
              display: 'flex',
              justifyContent: 'center',
              transition: 'all 0.3s ease-in-out',
              border: 'none',
              cursor: 'pointer',
              padding: '12px 0'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.01)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            Закрыть
          </button>
        </div>
      </Modal>
    </ConfigProvider>
  );
}

